/**
 * Pure helpers for the operating-area overlay. They mirror how the Backoffice
 * fleet map draws operating areas (`OperatingAreasPolygon` in fleet-maps-common):
 * all areas of a region are merged into one shape, and everything outside it is
 * greyed out.
 */
import { difference } from '@turf/difference'
import { featureCollection, polygon } from '@turf/helpers'
import { union } from '@turf/union'
import type {
  Feature,
  FeatureCollection,
  Geometry,
  MultiPolygon,
  Polygon,
} from 'geojson'

type AreaGeometry = MultiPolygon | Polygon
export type AreaFeature = Feature<AreaGeometry>

// Web Mercator can't show the poles, so the mask stops at its latitude limit.
const MERCATOR_MAX_LATITUDE = 85
const WORLD = polygon([
  [
    [-180, -MERCATOR_MAX_LATITUDE],
    [180, -MERCATOR_MAX_LATITUDE],
    [180, MERCATOR_MAX_LATITUDE],
    [-180, MERCATOR_MAX_LATITUDE],
    [-180, -MERCATOR_MAX_LATITUDE],
  ],
])

const isAreaGeometry = (
  geometry: Geometry | null | undefined,
): geometry is AreaGeometry =>
  geometry?.type === 'MultiPolygon' || geometry?.type === 'Polygon'

const toAreaFeature = (geometry: AreaGeometry): AreaFeature => ({
  type: 'Feature',
  properties: {},
  geometry,
})

const parseAreaGeometry = (geometry: string): AreaFeature | null => {
  try {
    const parsed: Geometry = JSON.parse(geometry)
    return isAreaGeometry(parsed) ? toAreaFeature(parsed) : null
  } catch {
    return null
  }
}

const mergeAreaFeatures = (features: AreaFeature[]): AreaFeature | null => {
  if (features.length === 0) return null
  if (features.length === 1) return features[0]
  return union(featureCollection(features))
}

/**
 * Merges the GeoJSON geometry strings of a region's operating areas into one
 * shape. Areas whose geometry can't be parsed are skipped. Returns null when no
 * area has a usable geometry.
 */
export const mergeOperatingAreas = (
  areas: ReadonlyArray<{ geometry: string }>,
): AreaFeature | null =>
  mergeAreaFeatures(
    areas
      .map(area => parseAreaGeometry(area.geometry))
      .filter(feature => feature !== null),
  )

/**
 * Merges the polygons of a hardcoded outline (the static geonet GeoJSON) into
 * one shape, the fallback when the live operating areas can't be loaded.
 */
export const mergeOutlineFeatures = (
  outline: FeatureCollection,
): AreaFeature | null =>
  mergeAreaFeatures(
    outline.features
      .map(feature => feature.geometry)
      .filter(isAreaGeometry)
      .map(toAreaFeature),
  )

/**
 * The whole world with the given areas cut out of it: the operating area, plus
 * any hardcoded geonet shown on top, so the shade only covers what is outside
 * all of them.
 */
export const buildOutsideMask = (
  areas: ReadonlyArray<AreaFeature>,
): AreaFeature | null => difference(featureCollection([WORLD, ...areas]))
